import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { NAV_ITEMS, roleDefaultPages } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

const PAGES = NAV_ITEMS.filter((i) => i.to !== "/");

export const PageAccessDialog = ({ target, roleDoc, onClose, onSaved }) => {
  const defaults = target ? roleDefaultPages(roleDoc, target.role) : [];
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (target) setSel(Array.isArray(target.page_access) ? target.page_access : defaults); }, [target]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (to) => setSel((s) => (s.includes(to) ? s.filter((x) => x !== to) : [...s, to]));
  const save = async (pages) => {
    setBusy(true);
    try {
      await api.patch(`/users/${target.id}/page-access`, { pages });
      toast.success(pages ? "Page access saved" : "Reset to role default");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="page-access-dialog">
        <DialogHeader><DialogTitle>Page Access — {target?.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">
          {Array.isArray(target?.page_access) ? "Custom access is on." : "Currently following their role."} Ticked pages show in their left menu. Pages marked <b>extra</b> are beyond their role: they can view them and do GM-level actions there. User Management as an extra is view-only in the menu — managing users still needs the Admin/Regional role.
        </p>
        <div className="divide-y divide-border border border-border">
          {PAGES.map((i) => {
            const extra = sel.includes(i.to) && !defaults.includes(i.to);
            const removed = !sel.includes(i.to) && defaults.includes(i.to);
            return (
              <label key={i.to} className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-secondary/60" data-testid={`page-access-${i.to.slice(1)}`}>
                <Checkbox checked={sel.includes(i.to)} onCheckedChange={() => toggle(i.to)} />
                <i.icon className="h-4 w-4" style={{ color: i.color }} />
                <span className="font-medium">{i.label}</span>
                {extra && <span className="ml-auto rounded bg-emerald-500/15 px-1.5 text-[0.65rem] font-semibold text-emerald-700 dark:text-emerald-400">extra</span>}
                {removed && <span className="ml-auto rounded bg-red-500/15 px-1.5 text-[0.65rem] font-semibold text-red-700 dark:text-red-400">removed</span>}
              </label>
            );
          })}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" onClick={() => save(null)} disabled={busy} data-testid="page-access-reset">Reset to role default</Button>
          <Button onClick={() => save(sel)} disabled={busy} data-testid="page-access-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
